import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { fetchLeaderboard, fetchOwnRank, type LeaderboardEntry, type OwnRankInfo } from "../lib/scores.js";
import type { DrawMode } from "@solitaire/game-engine";
import { useAuthStore } from "../store/useAuthStore.js";
import { useI18n } from "../i18n/index.js";

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

function RankBadge({ rank }: { rank: number }) {
  if (rank === 1) return <span className="text-yellow-400 font-bold">🥇</span>;
  if (rank === 2) return <span className="text-slate-300 font-bold">🥈</span>;
  if (rank === 3) return <span className="text-amber-600 font-bold">🥉</span>;
  return <span className="text-white/50">{rank}</span>;
}

export function LeaderboardPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { t, locale } = useI18n();

  const numFmt = locale === "de" ? "de-DE" : "en-US";

  const seedParam = params.get("seed");
  const seed = seedParam ? parseInt(seedParam, 10) : null;
  const drawModeParam = params.get("drawMode");
  const drawMode: DrawMode = drawModeParam === "3" ? 3 : 1;

  const [entries, setEntries] = useState<LeaderboardEntry[]>([]);
  const [ownRank, setOwnRank] = useState<OwnRankInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  const loadData = useCallback(() => {
    if (seed === null || isNaN(seed)) { setIsLoading(false); return; }
    setIsLoading(true);
    setHasError(false);
    setOwnRank(null);

    const promises: Promise<void>[] = [
      fetchLeaderboard(seed, drawMode).then(setEntries),
    ];
    if (user) {
      promises.push(fetchOwnRank(seed, user.id, drawMode).then(setOwnRank));
    }

    Promise.all(promises)
      .catch(() => setHasError(true))
      .finally(() => setIsLoading(false));
  }, [seed, user, drawMode]);

  useEffect(() => { loadData(); }, [loadData]);

  if (seed === null || isNaN(seed)) {
    return (
      <div className="min-h-screen bg-[var(--table-bg)] flex items-center justify-center text-white">
        <p>{t("lb.noGame")}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--table-bg)] text-white">
      {/* Header */}
      <div className="bg-black/20 px-4 py-3 flex items-center justify-between">
        <button
          onClick={() => navigate(-1)}
          className="text-white/60 hover:text-white text-sm transition-colors"
        >
          {t("lb.back")}
        </button>
        <div className="text-center">
          <h1 className="font-bold tracking-wider text-sm">{t("lb.title")}</h1>
          <p className="text-white/50 text-xs">{t("lb.gameNumber", { seed: seed.toLocaleString(numFmt) })}</p>
          <p className="text-white/40 text-[10px] uppercase tracking-widest">
            {drawMode === 3 ? "Klondike" : "Draw 1"}
          </p>
        </div>
        <button
          onClick={() => navigate(drawMode === 3 ? "/game?mode=klondike" : `/game?seed=${seed}`)}
          className="px-3 py-1 rounded bg-white/15 hover:bg-white/25 text-sm transition-colors"
        >
          {t("lb.play")}
        </button>
      </div>

      {/* Tabelle */}
      <div className="max-w-2xl mx-auto px-4 py-6">
        {isLoading ? (
          <p className="text-center text-white/40 py-12">{t("lb.loading")}</p>
        ) : hasError ? (
          <div className="text-center py-12">
            <p className="text-red-300 text-lg mb-3">{t("lb.error")}</p>
            <button
              onClick={loadData}
              className="px-5 py-2 rounded-lg bg-white/15 hover:bg-white/25 text-sm font-medium transition-colors"
            >
              {t("lb.retry")}
            </button>
          </div>
        ) : entries.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-white/40 text-lg mb-2">{t("lb.noEntries")}</p>
            <p className="text-white/30 text-sm">{t("lb.beFirst")}</p>
          </div>
        ) : (
          <>
          <div className="bg-white/5 rounded-xl overflow-hidden border border-white/10">
            {/* Spalten-Header (fixiert) */}
            <div className="grid grid-cols-[2.5rem_1fr_5rem_4rem_3.5rem] gap-0 px-4 py-2 bg-white/5 text-white/40 text-xs uppercase tracking-wider">
              <span>{t("lb.rank")}</span>
              <span>{t("lb.username")}</span>
              <span className="text-right">{t("lb.score")}</span>
              <span className="text-right">{t("lb.time")}</span>
              <span className="text-right">{t("lb.movesHeader")}</span>
            </div>

            {/* Scrollbare Einträge */}
            <div className="max-h-[65vh] overflow-y-auto">
              {entries.map((entry) => {
                const isOwn = user?.id === entry.userId;
                return (
                  <div
                    key={entry.userId}
                    className={[
                      "grid grid-cols-[2.5rem_1fr_5rem_4rem_3.5rem] gap-0 px-4 py-3 text-sm",
                      "border-t border-white/5",
                      isOwn
                        ? "bg-yellow-400/10 border-l-2 border-l-yellow-400/60"
                        : "hover:bg-white/5",
                    ].join(" ")}
                  >
                    <span className="flex items-center">
                      <RankBadge rank={entry.rank} />
                    </span>
                    <span className={[
                      entry.won ? "text-green-300" : "text-red-300",
                      isOwn ? "font-bold" : "",
                    ].join(" ")}>
                      {entry.username}
                      {isOwn && <span className="opacity-60 text-xs ml-1">{t("lb.you")}</span>}
                    </span>
                    <span className="text-right font-mono text-white/90">
                      {entry.score.toLocaleString(numFmt)}
                    </span>
                    <span className="text-right text-white/60 font-mono">
                      {formatTime(entry.elapsedSeconds)}
                    </span>
                    <span className="text-right text-white/60">
                      {entry.moves}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Eigene Platzierung (immer sichtbar unter der Tabelle) */}
          {ownRank && (
            <div className="mt-3 bg-white/5 rounded-xl border border-white/10 overflow-hidden">
              <div className="px-4 py-2 bg-white/5 text-white/40 text-xs uppercase tracking-wider">
                {t("lb.yourRank")}
              </div>
              <div className="grid grid-cols-[2.5rem_1fr_5rem_4rem_3.5rem] gap-0 px-4 py-3 text-sm bg-yellow-400/10 border-l-2 border-l-yellow-400/60">
                <span className="flex items-center">
                  <RankBadge rank={ownRank.rank} />
                </span>
                <span className={[
                  ownRank.entry.won ? "text-green-300" : "text-red-300",
                  "font-bold",
                ].join(" ")}>
                  {ownRank.entry.username}
                  <span className="opacity-60 text-xs ml-1">{t("lb.you")}</span>
                </span>
                <span className="text-right font-mono text-white/90">
                  {ownRank.entry.score.toLocaleString(numFmt)}
                </span>
                <span className="text-right text-white/60 font-mono">
                  {formatTime(ownRank.entry.elapsedSeconds)}
                </span>
                <span className="text-right text-white/60">
                  {ownRank.entry.moves}
                </span>
              </div>
              <div className="px-4 py-2 text-center text-white/30 text-xs">
                {t("lb.rankOfTotal", { rank: ownRank.rank.toString(), total: ownRank.total.toString() })}
              </div>
            </div>
          )}
          </>
        )}

        {entries.length > 0 && (
          <p className="text-center text-white/25 text-xs mt-4">
            {t("lb.footer", { count: entries.length })}
          </p>
        )}
      </div>
    </div>
  );
}
